// CFlowerBanner — 補花橫幅 overlay
// 開局發牌後若有任一家手上有花，進入補花階段時於畫面中央橫貫顯示：
//   - 一條粉紅漸層花帶（橫貫全寬）
//   - 沿花帶密集散落的梅花（暗紅 / 粉紅交錯，大小 / 位置帶隨機，貼近示意圖密度）
//   - 圓形「補花」徽章（從右飛入中央、補完再飛出左方）
// 視覺對齊參考：至尊麻將王 APP 補花畫面（花帶 + 中央徽章）。
//
// 動畫流程（三階段）：
//   進場 show()：
//     1. 花帶由中央往左右掃開 (scaleX 0→全寬) + 淡入
//     2. 花瓣各自隨機延遲，從 scale 0 彈跳綻放 + 淡入（此起彼落）
//     3. 徽章從畫面右側外飛入正中央 + 淡入落定 → onShown（同時開始實際補花）
//   停留：徽章輕微脈動，補花逐張替換動畫在各家手牌邊緣同時進行
//   退場 hide()（嚴格順序）：
//     1. 徽章從中央飛出左方 + 淡出
//     2. 花瓣各自隨機延遲，縮回 scale 0 + 淡出（不講順序）
//     3. 花帶最後淡出 → 銷毀 → onHidden
//
// === 使用圖片 ===
// - public/assets/ui/flower-band.png（key: FLOWER_BAND_KEY，54×108）— 直向粉紅漸層條，橫向拉伸成花帶底
// - public/assets/ui/flower-petal-a.png（key: FLOWER_PETAL_A_KEY，90×90）— 暗紅梅花
// - public/assets/ui/flower-petal-b.png（key: FLOWER_PETAL_B_KEY，90×90）— 粉紅梅花
// - public/assets/ui/flower-emblem.png（key: FLOWER_EMBLEM_KEY，270×198）— 圓形「補花」徽章

import type Phaser from 'phaser';

import { LOGICAL_HEIGHT, LOGICAL_WIDTH, SCREEN_CENTER_X } from '@config/game-config';

import { killAndDestroyContainer } from './tween-helpers';
import { UIDepth } from './ui-depths';
import {
  FLOWER_BAND_KEY,
  FLOWER_EMBLEM_KEY,
  FLOWER_PETAL_A_KEY,
  FLOWER_PETAL_B_KEY,
} from '@scenes/boot-scene';

// ---- 版面常數 ----
const BANNER_DEPTH = UIDepth.FlowerBanner; // 蓋過手牌 / 手勢 / 入場提示（ENTRANCE_HINT_DEPTH=200）
const BAND_CENTER_Y = LOGICAL_HEIGHT * 0.52; // 花帶中心 Y（略低於畫面正中，給徽章往上凸的空間）
const BAND_HEIGHT = 108; // 花帶顯示高度（flower-band 原圖高 108，略縮）

// ---- 花瓣（密集散落）----
const PETAL_COUNT = 22; // 沿花帶散落的梅花數（貼近示意圖密度）
const PETAL_SCALE_MIN = 0.7; // 花瓣目標縮放下限（原 90×90 → 約 45px）
const PETAL_SCALE_MAX = 1; // 花瓣目標縮放上限（→ 約 70px）
const PETAL_ROW_OFFSET = 12; // 相鄰花瓣上下交錯的基礎位移（px）
const PETAL_X_JITTER = 16; // 花瓣水平隨機抖動（px，± 範圍）
const PETAL_Y_JITTER = 10; // 花瓣垂直隨機抖動（px，± 範圍，疊加在交錯位移上）
const PETAL_DEPTH = 1; // 花瓣相對 container 的局部 depth（高於花帶底）

// ---- 徽章 ----
const EMBLEM_Y = BAND_CENTER_Y - 36; // 徽章中心 Y（往上凸出花帶）
const EMBLEM_HOME_X = SCREEN_CENTER_X; // 徽章停留位置（畫面正中）
const EMBLEM_OFFSCREEN_MARGIN = 280; // 徽章進 / 出場時停在畫面外的水平距離（px）
const EMBLEM_SCALE = 1.15; // 徽章縮放（原 270×198 → 約 311×228）
const EMBLEM_DEPTH = 2; // 徽章局部 depth（壓在花瓣 / 花帶之上）

// ---- 進場動畫常數 ----
const INTRO_BAND_MS = 340; // 花帶掃開 + 淡入時長
const INTRO_PETAL_MS = 360; // 單朵花瓣綻放（scale + fade）時長
const INTRO_PETAL_STAGGER_MAX = 300; // 花瓣進場隨機延遲上限（0 ~ 此值，造成此起彼落）
const INTRO_EMBLEM_MS = 480; // 徽章飛入時長
const INTRO_EMBLEM_DELAY_MS = 150; // 徽章較花瓣開始綻放稍晚飛入
const INTRO_EMBLEM_POP_FROM = 0.82; // 徽章飛入起始縮放（× EMBLEM_SCALE，落定回彈用）

// ---- 停留脈動 ----
const EMBLEM_PULSE_SCALE = 1.06; // 停留期間徽章脈動峰值（× EMBLEM_SCALE）
const EMBLEM_PULSE_MS = 700; // 徽章脈動單程時長

// ---- 退場動畫常數 ----
const OUTRO_EMBLEM_MS = 480; // 徽章飛出左方時長
const OUTRO_PETAL_MS = 360; // 單朵花瓣收合（scale + fade）時長
const OUTRO_PETAL_DELAY_MS = 220; // 花瓣開始收合相對徽章飛出的延遲
const OUTRO_PETAL_STAGGER_MAX = 300; // 花瓣退場隨機延遲上限（各自隨機收合）
const OUTRO_BAND_MS = 340; // 花帶最後淡出時長
const OUTRO_BAND_GAP_MS = 80; // 花瓣大致收完 → 花帶開始淡出的間隔

/** 一朵花瓣 sprite + 其目標縮放（綻放 / 收合都以此為基準）。 */
type TPetal = {
  readonly sprite: Phaser.GameObjects.Image;
  readonly targetScale: number;
};

/**
 * 補花橫幅。同一時間最多一個（show 前若殘留會先 destroy）。
 * 不持有補花業務邏輯，純視覺；由 CGameScene.runFlowerPhase 在補花階段前後驅動。
 */
export class CFlowerBanner {
  private container: Phaser.GameObjects.Container | null = null;
  private band: Phaser.GameObjects.Image | null = null;
  private emblem: Phaser.GameObjects.Image | null = null;
  private petals: TPetal[] = [];

  constructor(private readonly scene: Phaser.Scene) {}

  /** 是否正在顯示（避免重入）。 */
  isVisible(): boolean {
    return this.container !== null;
  }

  /** [min, max) 區間隨機（一般運行碼，非 Workflow，可用 Math.random）。 */
  private rand(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }

  /**
   * 進場：建立花帶 + 花瓣 + 徽章並播放進場動畫，徽章落定後 onShown。
   * 進場結束後徽章進入無限脈動，直到 hide()。
   */
  show(onShown: () => void): void {
    this.destroyNow(); // 清掉殘留（理論上不會有）

    const container = this.scene.add.container(0, 0).setDepth(BANNER_DEPTH);
    this.container = container;

    // ---- 建立花帶底（flower-band 橫向拉伸到全寬、縮到 BAND_HEIGHT）----
    const band = this.scene.add
      .image(LOGICAL_WIDTH / 2, BAND_CENTER_Y, FLOWER_BAND_KEY)
      .setDisplaySize(LOGICAL_WIDTH, BAND_HEIGHT)
      .setAlpha(0);
    container.add(band);
    this.band = band;

    // ---- 建立花瓣（密集、兩色交錯、大小 / 位置隨機；初始 scale 0 / alpha 0）----
    const margin = LOGICAL_WIDTH / (PETAL_COUNT + 1); // 兩端各留半格、平均分佈
    for (let i = 0; i < PETAL_COUNT; i++) {
      const x = margin * (i + 1) + this.rand(-PETAL_X_JITTER, PETAL_X_JITTER);
      const rowSign = i % 2 === 0 ? -1 : 1; // 相鄰交錯上 / 下
      const y =
        BAND_CENTER_Y + rowSign * PETAL_ROW_OFFSET + this.rand(-PETAL_Y_JITTER, PETAL_Y_JITTER);
      const key = i % 2 === 0 ? FLOWER_PETAL_A_KEY : FLOWER_PETAL_B_KEY;
      const sprite = this.scene.add
        .image(x, y, key)
        .setScale(0)
        .setAlpha(0)
        .setDepth(PETAL_DEPTH);
      container.add(sprite);
      this.petals.push({ sprite, targetScale: this.rand(PETAL_SCALE_MIN, PETAL_SCALE_MAX) });
    }

    // ---- 建立徽章（起始停在畫面右側外、alpha 0）----
    const emblem = this.scene.add
      .image(LOGICAL_WIDTH + EMBLEM_OFFSCREEN_MARGIN, EMBLEM_Y, FLOWER_EMBLEM_KEY)
      .setScale(EMBLEM_SCALE * INTRO_EMBLEM_POP_FROM)
      .setAlpha(0)
      .setDepth(EMBLEM_DEPTH);
    container.add(emblem);
    this.emblem = emblem;

    // ============ 進場動畫 ============
    // 1. 花帶由中央掃開 + 淡入（setDisplaySize 已把 scaleX 設成全寬倍率，先存起來再從 0 動畫過去）
    const bandFullScaleX = band.scaleX;
    band.scaleX = 0;
    this.scene.tweens.add({
      targets: band,
      scaleX: bandFullScaleX,
      alpha: 1,
      duration: INTRO_BAND_MS,
      ease: 'Sine.easeOut',
    });

    // 2. 花瓣各自隨機延遲綻放（scale 0 → 目標 + 淡入，Back.easeOut 彈跳）
    for (const petal of this.petals) {
      this.scene.tweens.add({
        targets: petal.sprite,
        scale: petal.targetScale,
        alpha: 1,
        duration: INTRO_PETAL_MS,
        delay: this.rand(0, INTRO_PETAL_STAGGER_MAX),
        ease: 'Back.easeOut',
      });
    }

    // 3. 徽章從右飛入中央 + 淡入落定（回彈），落定後開始脈動 + 通知 caller 開始補花
    this.scene.tweens.add({
      targets: emblem,
      x: EMBLEM_HOME_X,
      scale: EMBLEM_SCALE,
      alpha: 1,
      duration: INTRO_EMBLEM_MS,
      delay: INTRO_EMBLEM_DELAY_MS,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.startEmblemPulse();
        onShown();
      },
    });
  }

  /**
   * 退場（嚴格順序）：
   *   1. 徽章飛出左方 + 淡出
   *   2. 花瓣各自隨機延遲，縮回 scale 0 + 淡出
   *   3. 花帶最後淡出 → 銷毀 → onHidden
   * show 未呼叫時直接 onHidden。
   */
  hide(onHidden: () => void): void {
    if (!this.container) {
      onHidden();
      return;
    }

    // 1. 徽章：停脈動 → 從中央飛出左方 + 淡出
    if (this.emblem) {
      this.scene.tweens.killTweensOf(this.emblem);
      this.scene.tweens.add({
        targets: this.emblem,
        x: -EMBLEM_OFFSCREEN_MARGIN,
        alpha: 0,
        duration: OUTRO_EMBLEM_MS,
        ease: 'Back.easeIn',
      });
    }

    // 2. 花瓣：各自隨機延遲收合（scale → 0 + 淡出）
    let lastPetalDone = 0; // 追蹤最後一朵收完的時間點，決定花帶何時淡出
    for (const petal of this.petals) {
      const delay = OUTRO_PETAL_DELAY_MS + this.rand(0, OUTRO_PETAL_STAGGER_MAX);
      lastPetalDone = Math.max(lastPetalDone, delay + OUTRO_PETAL_MS);
      this.scene.tweens.add({
        targets: petal.sprite,
        scale: 0,
        alpha: 0,
        duration: OUTRO_PETAL_MS,
        delay,
        ease: 'Back.easeIn',
      });
    }

    // 3. 花帶：等花瓣大致收完才淡出，淡完銷毀 + onHidden
    this.scene.time.delayedCall(lastPetalDone + OUTRO_BAND_GAP_MS, () => {
      if (!this.band) {
        this.destroyNow();
        onHidden();
        return;
      }
      this.scene.tweens.add({
        targets: this.band,
        alpha: 0,
        duration: OUTRO_BAND_MS,
        ease: 'Sine.easeIn',
        onComplete: () => {
          this.destroyNow();
          onHidden();
        },
      });
    });
  }

  /** 徽章停留脈動（無限 yoyo），維持畫面有生命感。 */
  private startEmblemPulse(): void {
    if (!this.emblem) return;
    this.scene.tweens.add({
      targets: this.emblem,
      scale: EMBLEM_SCALE * EMBLEM_PULSE_SCALE,
      duration: EMBLEM_PULSE_MS,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /** 立即銷毀容器與動畫（不走退場）。 */
  private destroyNow(): void {
    // 花帶 / 花瓣 / 徽章都是 container 的直接子物件，killAndDestroyContainer 會逐一 kill tween 再 destroy(true)
    killAndDestroyContainer(this.scene, this.container);
    this.container = null;
    this.emblem = null;
    this.band = null;
    this.petals = [];
  }
}
